import { useState, useMemo } from 'react';
import { IcoCash, IcoCard, IcoTransfer, IcoCheck } from './common/Icons.jsx';
import styles from './SplitBillSheet.module.css';

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const r2  = (n) => Math.round(n * 100) / 100;

const METHODS = [
  { key: 'cash',     label: 'Efectivo',      Icon: IcoCash     },
  { key: 'card',     label: 'Tarjeta',       Icon: IcoCard     },
  { key: 'transfer', label: 'Transferencia', Icon: IcoTransfer },
];

/**
 * Reparte el total entre n partes.
 * El redondeo sobrante se lo lleva la ÚLTIMA parte, para que la suma cuadre
 * al centavo: $100 entre 3 son 33.33 + 33.33 + 33.34, no 99.99.
 */
export const evenSplit = (total, n) => {
  const base  = Math.floor((total / n) * 100) / 100;
  const parts = Array.from({ length: n }, () => base);
  parts[n - 1] = r2(total - base * (n - 1));
  return parts;
};

/**
 * Cuenta dividida. Un mismo diseño cubre partes iguales y montos libres:
 * se reparte parejo por defecto y cada monto queda editable.
 */
export default function SplitBillSheet({ total, onClose, onCharge, paid = [], busyIndex = null, porComensal = [] }) {
  // Con comensales capturados se arranca en "por consumo": si el mesero se
  // tomó el trabajo de anotar de quién es cada platillo, dividir parejo tira
  // ese trabajo a la basura. Igual se puede cambiar a partes iguales.
  const hayComensales = porComensal.length > 1;
  const [modo, setModo] = useState(hayComensales ? 'consumo' : 'iguales');

  const [count,   setCount]   = useState(hayComensales ? porComensal.length : 2);
  const [amounts, setAmounts] = useState(() => (hayComensales
    ? porComensal.map((p) => p.total)
    : evenSplit(total, 2)));
  const [methods, setMethods] = useState(() => ['cash', 'cash']);
  const [touched, setTouched] = useState(false);   // ¿el cajero editó algún monto?

  // Cambiar de modo rehace el reparto: por consumo son los comensales que hay,
  // por partes iguales lo que el cajero elija.
  const cambiarModo = (m) => {
    setModo(m);
    setTouched(false);
    if (m === 'consumo') {
      setCount(porComensal.length);
      setAmounts(porComensal.map((p) => p.total));
      setMethods(porComensal.map((_, i) => methods[i] ?? 'cash'));
    } else {
      setCount(2);
      setAmounts(evenSplit(total, 2));
      setMethods(['cash', 'cash']);
    }
  };

  const setParts = (n) => {
    if (n < 2 || n > 12) return;
    setCount(n);
    setAmounts(evenSplit(total, n));
    setMethods(Array.from({ length: n }, (_, i) => methods[i] ?? 'cash'));
    setTouched(false);
  };

  const setAmount = (idx, value) => {
    const next = [...amounts];
    next[idx] = Math.max(0, Number(value) || 0);
    setAmounts(next);
    setTouched(true);
  };

  const setMethod = (idx, m) => {
    const next = [...methods];
    next[idx] = m;
    setMethods(next);
  };

  const suma      = useMemo(() => r2(amounts.reduce((a, b) => a + b, 0)), [amounts]);
  const diferencia = r2(total - suma);
  const cuadra    = Math.abs(diferencia) < 0.01;

  const pagadoTotal = useMemo(
    () => r2(paid.reduce((acc, p) => acc + (p?.amount ?? 0), 0)),
    [paid]
  );
  const todoPagado = paid.length >= count && paid.every(Boolean);

  // Una vez cobrada la primera parte ya no se puede cambiar el reparto:
  // el dinero de esa persona ya entró y movería el cuadre.
  const bloqueado = paid.some(Boolean);

  return (
    <div className={styles.backdrop} onClick={busyIndex == null ? onClose : undefined}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>

        <div className={styles.header}>
          <div>
            <p className={styles.title}>Dividir cuenta</p>
            <p className={styles.subtitle}>Total {fmt(total)}</p>
          </div>
          <button
            className={styles.close}
            onClick={onClose}
            disabled={busyIndex != null}
            aria-label="Cerrar"
          >✕</button>
        </div>

        {/* Cómo se divide. Solo aparece si hay comensales capturados: sin ellos
            no hay nada que elegir. */}
        {hayComensales && (
          <div className={styles.modos}>
            <button
              className={modo === 'consumo' ? styles.modoOn : styles.modo}
              onClick={() => cambiarModo('consumo')}
              disabled={bloqueado}
            >
              Lo que consumió cada quien
            </button>
            <button
              className={modo === 'iguales' ? styles.modoOn : styles.modo}
              onClick={() => cambiarModo('iguales')}
              disabled={bloqueado}
            >
              Partes iguales
            </button>
          </div>
        )}

        {/* Número de partes. Por consumo lo fijan los comensales de la mesa. */}
        {modo === 'consumo' ? (
          <p className={styles.notaConsumo}>
            {count} comensales. Lo compartido va repartido entre ellos.
          </p>
        ) : (
        <div className={styles.countRow}>
          <span className={styles.countLabel}>¿Entre cuántos?</span>
          <div className={styles.stepper}>
            <button onClick={() => setParts(count - 1)} disabled={bloqueado || count <= 2}>−</button>
            <span className={styles.countNum}>{count}</span>
            <button onClick={() => setParts(count + 1)} disabled={bloqueado || count >= 12}>+</button>
          </div>
        </div>
        )}

        {touched && !bloqueado && (
          <button className={styles.resetLink} onClick={() => setParts(count)}>
            Repartir en partes iguales
          </button>
        )}

        {/* Partes */}
        <div className={styles.body}>
          {amounts.map((amount, idx) => {
            const yaPagada = !!paid[idx];
            const cobrando = busyIndex === idx;
            return (
              <div key={idx} className={`${styles.part} ${yaPagada ? styles.partPaid : ''}`}>
                <div className={styles.partHead}>
                  {/* Con nombre y no "Parte 1". El cajero tiene que poder
                      verificar contra la mesa de quién es cada monto, y el
                      cliente preguntar "¿qué me tocó?" y obtener respuesta: sin
                      esto, todo el trabajo de capturar por comensal terminaba
                      en dos números anónimos. */}
                  <span className={styles.partName}>
                    {modo === 'consumo' && porComensal[idx]?.nombre
                      ? porComensal[idx].nombre
                      : `Parte ${idx + 1}`}
                  </span>
                  {yaPagada && (
                    <span className={styles.partDone}>
                      <IcoCheck size={12} /> Pagada
                    </span>
                  )}
                </div>

                {/* Qué consumió, para poder cotejarlo con la mesa. El dato ya
                    venía en `porComensal` y no se estaba usando. */}
                {modo === 'consumo' && porComensal[idx]?.items?.length > 0 && (
                  <p className={styles.partItems}>
                    {/* Se arma como lista y se junta al final: quien solo tiene
                        parte de lo compartido —no pidió nada suyo— quedaba con
                        un separador suelto delante, "· compartido $47.50". */}
                    {[
                      ...porComensal[idx].items
                        .filter((i) => (i.seats ?? []).length === 1)
                        .map((i) => `${i.quantity}× ${i.product?.name ?? ''}`),
                      ...(porComensal[idx].compartido > 0 ? [`compartido ${fmt(porComensal[idx].compartido)}`] : []),
                    ].join(' · ')}
                  </p>
                )}

                <div className={styles.partRow}>
                  <div className={styles.amountWrap}>
                    <span className={styles.currency}>$</span>
                    <input
                      className="input"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={yaPagada ? paid[idx].amount : amount}
                      disabled={yaPagada || bloqueado}
                      onChange={(e) => setAmount(idx, e.target.value)}
                    />
                  </div>
                </div>

                {!yaPagada && (
                  <>
                    <div className={styles.methodRow}>
                      {METHODS.map((m) => (
                        <button
                          key={m.key}
                          className={`${styles.methodBtn} ${methods[idx] === m.key ? styles.methodBtnActive : ''}`}
                          onClick={() => setMethod(idx, m.key)}
                        >
                          <m.Icon size={15} />
                          <span>{m.label}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      className={`btn btn--primary btn--full ${styles.chargePart}`}
                      disabled={!cuadra || amount <= 0 || busyIndex != null}
                      onClick={() => onCharge({ index: idx, amount, method: methods[idx], count, amounts, modo })}
                    >
                      {cobrando
                        ? <><span className="spinner" /> Cobrando…</>
                        : `Cobrar ${fmt(amount)}`}
                    </button>
                  </>
                )}

                {yaPagada && (
                  <p className={styles.partMethod}>
                    {METHODS.find((m) => m.key === paid[idx].method)?.label ?? paid[idx].method}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Pie con el cuadre */}
        <div className={styles.footer}>
          {!cuadra && (
            <p className={styles.mismatch}>
              {diferencia > 0
                ? <>Faltan <strong>{fmt(diferencia)}</strong> por repartir</>
                : <>Te pasaste por <strong>{fmt(Math.abs(diferencia))}</strong></>}
            </p>
          )}
          <div className={styles.summary}>
            <span>Cobrado {fmt(pagadoTotal)} de {fmt(total)}</span>
            {todoPagado && <strong className={styles.allPaid}>Completa</strong>}
          </div>
        </div>

      </div>
    </div>
  );
}
